// Compact in-header player search for needs-player widgets. Type ≥3 chars → a
// dropdown of matches; picking one hands back a resolved PlayerRef (id + most-
// recent comp/season). Reuses the v1 usePlayerSearch hook + the /player-search
// endpoint — the data layer is a keeper (docs/frontend-v2/ keep list).

import { useRef, useState } from "react";
import { usePlayerSearch } from "../../data/players.js";
import { refFromSearch } from "../playerRef.js";
import type { PlayerRef } from "../types.js";
import { theme } from "../theme.js";

export default function PlayerSearch({
  currentName,
  onPick,
}: {
  currentName: string | null;
  onPick: (ref: PlayerRef) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const searchQ = usePlayerSearch(query);
  const results = searchQ.data?.players ?? [];

  return (
    <div ref={boxRef} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        value={query}
        placeholder={currentName ?? "Search a player…"}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Delay close so a click on a result registers first.
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        // Don't let a click in the input start a widget drag (the header is a
        // drag handle).
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: theme.field,
          border: `1px solid ${theme.border}`,
          borderRadius: 6,
          color: theme.ink,
          fontSize: 12,
          padding: "3px 8px",
          outline: "none",
        }}
      />
      {open && query.trim().length >= 3 ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: theme.tileHeader,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            maxHeight: 220,
            overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          }}
        >
          {searchQ.isLoading ? (
            <div style={rowStyle}>Searching…</div>
          ) : results.length === 0 ? (
            <div style={rowStyle}>No players found.</div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => {
                  onPick(refFromSearch(r));
                  setQuery("");
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  color: theme.ink,
                  fontSize: 12,
                  padding: "6px 8px",
                  cursor: "pointer",
                }}
              >
                <span>{r.name}</span>
                {r.team ? <span style={{ color: theme.inkFaint }}> · {r.team}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

const rowStyle: React.CSSProperties = { padding: "6px 8px", color: theme.inkMuted, fontSize: 12 };
